import { defineComponent, h } from 'vue';

import { getCellFixedInfo, getColumnsKey } from '../../utils';
import { Cell } from '../Cell';

import DragHandle from './DragHandle';

import type { NTable } from '../../interface';

interface IHeaderRowProps<RecordType = NTable.TDefaultRecord> {
  cells: readonly NTable.NCell.ICell<RecordType>[];
  flattenColumns: readonly NTable.NColumn.IColumn<RecordType>[];
  stickyOffsets: NTable.NSticky.IStickyOffsets;
  rowComponent: NTable.NCustomize.TCustomizeComponent;
  cellComponent: NTable.NCustomize.TCustomizeComponent;
  index: number;
  customHeaderRow: NTable.NCustomize.TGetComponentProps<readonly NTable.NColumn.IColumn<RecordType>[]>;
}

export default defineComponent<IHeaderRowProps>({
  name: 'HeaderRow',
  props: [
    'cells',
    'flattenColumns',
    'stickyOffsets',
    'rowComponent',
    'cellComponent',
    'index',
    'customHeaderRow'
  ] as any,
  setup(props) {
    return () => {
      const {
        cells,
        stickyOffsets,
        flattenColumns,
        rowComponent: RowComponent,
        cellComponent: CellComponent,
        index,
        customHeaderRow
      } = props;

      let rowProps;
      if (customHeaderRow) {
        rowProps = customHeaderRow(
          cells.map((cell) => cell.column),
          index
        );
      }

      const columnsKey = getColumnsKey(cells.map((cell) => cell.column));

      return h(
        RowComponent,
        { ...rowProps },
        cells.map((cell, cellIndex) => {
          const { column } = cell;
          const fixedInfo = getCellFixedInfo(
            cell.colStart as number,
            cell.colEnd as number,
            flattenColumns,
            stickyOffsets
          );

          let additionalProps;

          if (column && column.customHeaderCell) {
            additionalProps = cell.column.customHeaderCell?.(column);
          }

          const col: NTable.NColumn.IColumn = column;

          return h(
            Cell,
            {
              ...cell,
              cellType: 'header',
              ellipsis: column.ellipsis,
              align: column.align,
              component: CellComponent,
              key: columnsKey[cellIndex],
              ...fixedInfo,
              rowType: 'header',
              additionalProps,
              column
            },
            {
              default: () => column.title,
              dragHandle: () =>
                col.resizable
                  ? h(DragHandle, {
                      column: col,
                      width: col.width as number,
                      minWidth: col.minWidth,
                      maxWidth: col.maxWidth
                    })
                  : null
            }
          );
        })
      );
    };
  }
});
